import type {ReactNode} from 'react'
import {Link} from 'react-router'

import type {PackIndexItem, SeriesGroup} from '@core/packs/series/types'
import type {HouseHome, HouseIdentity} from '@core/wall/home/doorstep'
import {Footer} from '@product/toile/components/Footer/Footer'
import {livePacks, getPackBySlug} from '@product/toile/packs/registry'
import packIndexStyles from '@product/toile/styles/Packs/packIndex.module.scss'
import {toileTheme} from '@product/toile/wall/theme'

import faq from './faq.json'
import styles from './endpaper.module.scss'
import {surfaceOgImageUrl} from '@core/og/cardUrls'
import {toileConfig} from '@product/toile/config'

/**
 * Toile's home doorstep (plan `2026_09_01_toile_homepage_seo.md`, copy verbatim from its § 5; the
 * doorstep shape from `2026_09_24_wall_home_doorstep.md`). Registry-derived at module scope — the
 * one loader-computed input is the full-catalog `packIndexGroups`, which needs the server-only
 * series classifier.
 */
const ORIGIN = 'https://toile.pictures'
const CATALOG_LIMIT = 24
const totalPacks = livePacks.length

export const toileHouse: HouseIdentity = {
    origin: ORIGIN,
    meta: {title: toileConfig.meta.title, description: toileConfig.meta.description},
    organization: {
        name: 'Toile',
        logo: `${ORIGIN}/toile-web-app-manifest-512x512.png`,
        description: 'A catalog of general-purpose image-generation packs, from the house behind Narcis and Ovid.',
    },
    webApplication: {
        name: 'Toile — image generation, twisted from a preset',
        alternateName: ['Toile', 'Toile Pictures'],
        description:
            'An endless wall of generated pictures — search it, twist any picture with a few words, or ' +
            'start from nothing.',
        url: ORIGIN,
        applicationCategory: 'MultimediaApplication',
        offers: {price: '0', priceCurrency: 'EUR', description: 'Free to browse and generate'},
        featureList: livePacks.map((p) => p.name),
        screenshot: surfaceOgImageUrl(ORIGIN, 'home'),
    },
    tileImageUrl: toileTheme.tileImageUrl,
}

export function toileHome(packIndexGroups: SeriesGroup<PackIndexItem>[]): HouseHome {
    return {
        ...toileHouse,
        styles,
        endpaper: {
            intro: {
                h1: 'Toile — free AI image generator',
                lede:
                    'Toile is a wall of ready pictures — coloring pages, wallpapers, sticker designs, logos, seamless ' +
                    'patterns, posters, tattoo designs and more, all made by AI. Search the wall, take any picture ' +
                    'and change it with a few words in your own language, or start from nothing. No prompt ' +
                    'engineering, no account to browse, free to generate.',
                stats: `${totalPacks} packs · ${totalPacks * 9} pictures to start from`,
            },
            catalog: {
                h2: 'Packs',
                headerLinks: [{label: `See all ${totalPacks} packs →`, href: '/packs'}],
                links: livePacks.slice(0, CATALOG_LIMIT).map((p) => ({label: p.name, href: `/${p.slug}`})),
            },
            sections: SECTIONS,
            faq: {h2: 'Questions', entries: faq},
            packIndex: {groups: packIndexGroups, total: totalPacks, styles: packIndexStyles},
            coda: (
                <>
                    <FeaturedOn />
                    <Footer />
                </>
            ),
        },
    }
}

/**
 * Category-word -> pack links for the second section only (plan § 3). Filtered through
 * `getPackBySlug` at module scope: a renamed or retired slug drops the link instead of pointing at
 * a 404.
 */
interface CategoryLink {
    phrase: string
    slug: string
}

const CANDIDATE_CATEGORY_LINKS: CategoryLink[] = [
    {phrase: 'coloring pages', slug: 'coloring-pages'},
    {phrase: 'wallpapers', slug: 'wallpapers'},
    {phrase: 'sticker designs', slug: 'stickers'},
    {phrase: 'logos', slug: 'logo-designs'},
    {phrase: 'icon sets', slug: 'icon-sets'},
    {phrase: 'seamless patterns', slug: 'seamless-patterns'},
    {phrase: 'tattoo designs', slug: 'tattoo-designs'},
    {phrase: 'wall art', slug: 'wall-art'},
    {phrase: 'food photography', slug: 'food-photography'},
]

const CATEGORY_LINKS = CANDIDATE_CATEGORY_LINKS.filter((l) => !!getPackBySlug(l.slug))

function escapeForRegExp(s: string): string {
    return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** Splits `text` on the known category phrases (longest first, so no phrase can be shadowed by a
 *  shorter one sharing a prefix) and wraps each match in a `<Link>` to its pack. */
function linkedCopy(text: string, links: CategoryLink[]): ReactNode[] {
    if (links.length === 0) return [text]
    const ordered = [...links].sort((a, b) => b.phrase.length - a.phrase.length)
    const pattern = new RegExp(`(${ordered.map((l) => escapeForRegExp(l.phrase)).join('|')})`, 'g')
    return text.split(pattern).map((part, i) => {
        const link = links.find((l) => l.phrase === part)
        return link ? (
            <Link key={i} to={`/${link.slug}`}>
                {part}
            </Link>
        ) : (
            part
        )
    })
}

const SECTIONS = [
    {
        h2: 'Start from a picture, not a blank page',
        body: (
            <p>
                Most AI image generators hand you an empty prompt box. Toile hands you a wall: thousands of finished
                pictures organised into packs, each one a working recipe. Find the closest picture to what you want,
                tap it, and say what should change — &ldquo;make it a fox&rdquo;, &ldquo;night instead of
                day&rdquo;, &ldquo;in watercolour&rdquo;. The picture keeps everything you didn&rsquo;t mention.
            </p>
        ),
    },
    {
        h2: 'Every kind of picture, ready to use',
        body: (
            <p>
                {linkedCopy(
                    'The wall covers the deliverables people actually search for: printable coloring ' +
                        'pages, phone and desktop wallpapers, sticker designs with clean cut lines, logos ' +
                        'and icon sets, seamless patterns for fabric and wrapping, invitation cards, ' +
                        'tattoo designs, posters and wall art, food photography. Each pack holds nine ' +
                        'variations on one idea, and every picture on the wall can be twisted into your ' +
                        'own version.',
                    CATEGORY_LINKS,
                )}
            </p>
        ),
    },
    {
        h2: 'Edit images with words',
        body: (
            <p>
                Toile is also an AI photo and image editor that works in plain language. A short direction — a few
                words, any language — changes exactly one thing: the subject, the colours, the medium, the mood. Ask
                again for a new variation, or keep turning the same picture until it is yours. Everything you make
                lands back on the wall, searchable by the words you used.
            </p>
        ),
    },
]

/* -------------------------------------------------------------------------- */
/*  Featured-on — directory badge strip, narcis's pattern. A badge is a       */
/*  dofollow link out of our homepage; it stays only while the directory      */
/*  returns a dofollow, indexable link to toile.pictures. Add a directory =   */
/*  one array entry, the markup the directory issued.                          */
/* -------------------------------------------------------------------------- */

const FEATURED_ON = [
    {
        href: 'https://findly.tools/toile?utm_source=toile',
        src: 'https://findly.tools/badges/findly-tools-badge-light.svg',
        alt: 'Featured on Findly.tools',
        rel: 'noopener noreferrer',
        width: 175,
        height: 55,
    },
    {
        href: 'https://fazier.com',
        src: 'https://fazier.com/api/v1//public/badges/launch_badges.svg?badge_type=featured&theme=neutral',
        alt: 'Fazier badge',
        rel: 'noopener',
        width: 182,
        height: 43,
    },
    {
        href: 'https://www.toolpilot.ai',
        src: 'https://www.toolpilot.ai/cdn/shop/files/f-w_690x151_crop_center.png',
        alt: 'Featured on ToolPilot',
        rel: 'noopener',
        width: 690,
        height: 151,
    },
    {
        href: 'https://auraplusplus.com/projects/toile',
        src: 'https://auraplusplus.com/images/badges/featured-on-light.svg',
        alt: 'Featured on Aura++',
        rel: 'noopener',
        width: 150,
        height: 44,
    },
]

function FeaturedOn() {
    return (
        <section className={styles.featuredOnSection}>
            <span className={styles.featuredOnLabel}>Featured on</span>
            <div className={styles.featuredOn}>
                {FEATURED_ON.map((b) => (
                    <a key={b.href} href={b.href} target="_blank" rel={b.rel}>
                        <img
                            className={styles.featuredOnBadge}
                            src={b.src}
                            alt={b.alt}
                            width={b.width}
                            height={b.height}
                            loading="lazy"
                            decoding="async"
                        />
                    </a>
                ))}
            </div>
        </section>
    )
}
